import { FC } from 'react';
import { Link } from 'react-router-dom';
import { rem } from 'polished';
import { Button, Flex, Modal as RooModal, Paragraph } from 'roo-ui/components';
import {
  CUSTOMER_SUPPORT_CALL_HOURS,
  CUSTOMER_SUPPORT_CALL_DAYS,
  CUSTOMER_SUPPORT_HOTLINE_AU,
  CUSTOMER_SUPPORT_HOTLINE_INTL,
} from 'config/constants';
import { useDetailsBooking } from 'lib/hooks';
import { getUpdatePaymentMethodPageLink } from 'lib/links';
import * as track from 'lib/gaTracking/bookingDetailsPageEvents';
import { Modal, ModalFooter } from './primitives';
import TelLink from 'components/TelLink';
import { useBookingDetailsPageContext } from '../../../contextProvider/BookingDetailsPageContextProvider';

const ActionablePaymentMethodUpdateRequiredModal: FC = () => {
  const { booking } = useDetailsBooking();
  const {
    modals: {
      adyenMigrationActionableModal: { isActiveModal, isOpen, closeModal },
    },
  } = useBookingDetailsPageContext();
  const updatePaymentMethodLink = getUpdatePaymentMethodPageLink(booking.id);
  const close = () => {
    track.updatePaymentDetailsModalDismissSelected();
    closeModal();
  };

  if (!isActiveModal) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} ariaHideApp={false}>
      <RooModal.header variant="error">
        <Paragraph textAlign="center">Payment details must be updated before any changes or cancellations can be made.</Paragraph>
      </RooModal.header>
      <RooModal.body pb={[rem(160), 0]} textAlign="left">
        <Paragraph pb={2}>
          Due to an upgrade to our payment system, we are no longer storing your credit card details. To make any changes or to cancel your
          booking you will need to re-enter your payment details to ensure any changes to the booking price can be charged or refunded as
          required.
        </Paragraph>

        <Paragraph pb={8}>
          Please follow the link below to update your payment details or contact us. Our support team is available{' '}
          {CUSTOMER_SUPPORT_CALL_HOURS}, {CUSTOMER_SUPPORT_CALL_DAYS} on{' '}
          <TelLink
            data-testid="call-support-link"
            number={CUSTOMER_SUPPORT_HOTLINE_AU}
            color="greys.steel"
            underline
            onClick={() => track.updatePaymentDetailsModalLinkSelected('call customer support')}
          />
          , or outside Australia:{' '}
          <TelLink
            data-testid="call-international-support-link"
            number={CUSTOMER_SUPPORT_HOTLINE_INTL}
            color="greys.steel"
            underline
            onClick={() => track.updatePaymentDetailsModalLinkSelected('call international customer support')}
          />
        </Paragraph>
      </RooModal.body>
      <ModalFooter py={[4, 8]}>
        <Flex flexDirection={['column', 'row']} justifyContent="space-between" alignItems="stretch" width="100%">
          <Button
            variant="primary"
            as={Link}
            to={updatePaymentMethodLink}
            mb={[2, 0]}
            mr={[0, 6]}
            width={['100%', '65%']}
            onClick={track.updatePaymentDetailsModalUpdateSelected}
          >
            UPDATE PAYMENT DETAILS
          </Button>
          <Button onClick={close} width={['100%', '35%']}>
            NO THANKS
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default ActionablePaymentMethodUpdateRequiredModal;
